import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DesignPartnerDomainsService {
  private apiUrl = `${environment.BACKEND_URL}/design-partner`;
  private certKitUrl = 'https://ct.certkit.io/search';

  constructor(private http: HttpClient) {}

  getDomains(): Observable<any> {
    return this.http.get(`${this.apiUrl}/domains`);
  }

  addDomain(domain: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/domains`, { domain });
  }

  searchCertificates(domain: string): Observable<any> {
    return this.http.post(this.certKitUrl, { domain, sort: '' });
  }

  saveSubDomains(domain: string, records: any[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/sub-domains`, { domain, records });
  }

  getSubDomains(domain: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/sub-domains`, { params: { domain } });
  }

  getTlsInfo(subdomain: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/tls`, { params: { domain: subdomain } });
  }

  getMonitoredDomains(): Observable<any> {
    return this.http.get(`${this.apiUrl}/monitor`);
  }

  startMonitoring(record: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/monitor`, record);
  }

  refreshMonitoring(record: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/monitor`, record);
  }

  stopMonitoring(domain: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/monitor`, { params: { domain } });
  }

  /**
   * Queries certkit for the given domain, picks the record with the most recent
   * insertedTime, and augments it with TLS support + computed riskLevel so it can be
   * sent straight to the monitor endpoint or rendered in the certificate detail modal.
   */
  buildLatestMonitoringRecord(domain: string): Observable<any> {
    return this.searchCertificates(domain).pipe(
      switchMap((response: any) => {
        const results: any[] = response?.results ?? [];
        const latest = results.reduce((latest: any, record: any) => {
          if (!record?.insertedTime) return latest;
          return !latest || new Date(record.insertedTime) > new Date(latest.insertedTime) ? record : latest;
        }, null);

        if (!latest) {
          return of(null);
        }

        return this.getTlsInfo(latest.commonName).pipe(
          catchError(() => of({ tls: [] })),
          map((tlsRes: any) => {
            const tls = tlsRes?.tls ?? [];
            return { ...latest, tls, riskLevel: this.calculateRisk(latest, tls) };
          })
        );
      })
    );
  }

  private getDaysRemaining(notAfter: string): number {
    return Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  private calculateRisk(cert: any, tls: any[]): string {
    const days = this.getDaysRemaining(cert.notAfter);
    const insecureTls = tls.some(
      (t: any) => (t.version === 'TLSv1' || t.version === 'TLSv1.1') && t.supported
    );
    if (days < 0 || days <= 14) return 'CRITICAL';
    if (days <= 30 || insecureTls) return 'HIGH';
    if (days <= 90) return 'MEDIUM';
    return 'LOW';
  }
}
